import React from 'react';
import classNames from 'classnames';

import './tooltips.scss';

import { useAlt, useCtrl, useShift } from 'ui/modifiers';

import Data from 'data';
import Tooltip, { RawTooltip } from 'ui/tooltip';
import GemTooltip from './GemTooltip';
import ItemTooltip, { SetTooltip, StatTooltip } from './ItemTooltip';
import SkillTooltip, { FollowerSkillTooltip, BuffTooltip, AltarTooltip } from './SkillTooltip';
import Store from 'store';
import { itemAllowed } from 'logic/item';
import { formatNumber } from 'utils/format';
import NumberInput from 'ui/number';

const D3Tooltip = ({ tooltip, ...props }) => <RawTooltip {...props} tooltip={(
  <div className="d3-tooltip-wrapper">
    <div className="d3-tooltip-wrapper-inner">{tooltip}</div>
  </div>
)}/>;

function ItemTooltipSingle(props) {
  const ctrlKey = useCtrl();
  const shiftKey = useShift();
  return (
    <div className={classNames("d3-tooltip-wrapper", {"d3-tooltip-showrange": ctrlKey, "d3-tooltip-perfection": shiftKey})}>
      <div className="d3-tooltip-wrapper-inner">
        <ItemTooltip {...props}/>
      </div>
    </div>
  );
}

function ItemTooltipWrapped({ data, slot, follower, soulShard }) {
  const profile = Store.useProfile();
  const items = follower ? profile.followerItems : profile.items;
  const altKey = useAlt();
  if (data && (typeof data === "object" && !data.template) && (!slot || items[slot] !== data)) {
    if (!slot) {
      const id = (typeof data === "object" ? data.id : data);
      slot = Object.keys(items).find(slot => items[slot] && items[slot].id === id);
      if (!slot) {
        const slots = Object.keys(items).filter(slot => items[slot] && itemAllowed(profile, slot, id, follower));
        if (slots.length) {
          slot = slots[0];
          if (slots.length >= 2 && altKey) {
            slot = slots[1];
          }
        }
      }
    }
    if (slot && items[slot] !== data) {
      return (
        <React.Fragment>
          <ItemTooltipSingle data={data} slot={slot} compare={slot} follower={follower}/>
          <ItemTooltipSingle data={items[slot]} slot={slot} compare={true} follower={follower}/>
        </React.Fragment>
      );
    }
  }
  return <ItemTooltipSingle data={data} slot={slot} follower={follower} soulShard={soulShard}/>;
}

export function D3TooltipGem({ id, level, socketType, ...props }) {
  return <D3Tooltip {...props} tooltip={<GemTooltip id={id} level={level} socketType={socketType}/>}/>;
}
export function D3TooltipItem({ data, slot, follower, soulShard, ...props }) {
  return <RawTooltip {...props} tooltip={<ItemTooltipWrapped data={data} slot={slot} follower={follower} soulShard={soulShard}/>}/>;
}
export function D3TooltipSet({ id, ...props }) {
  return <D3Tooltip {...props} tooltip={<SetTooltip id={id}/>}/>;
}
export function D3TooltipStat({ id, ...props }) {
  return <D3Tooltip {...props} tooltip={<StatTooltip id={id}/>}/>;
}
export function D3TooltipSkill({ charClass, skill, rune, onlyRune, ...props }) {
  return <D3Tooltip {...props} tooltip={<SkillTooltip charClass={charClass} skill={skill} rune={rune} onlyRune={onlyRune}/>}/>;
}
export function D3TooltipFollowerSkill({ id, ...props }) {
  return <D3Tooltip {...props} tooltip={<FollowerSkillTooltip id={id}/>}/>;
}
export function D3TooltipBuff({ id, ...props }) {
  return <D3Tooltip {...props} tooltip={<BuffTooltip id={id}/>}/>;
}
export function D3TooltipAltar({ id, ...props }) {
  return <D3Tooltip {...props} tooltip={<AltarTooltip id={id}/>}/>;
}

export function D3TooltipCube({ id, ...props }) {
  const item = id && Data.itemById[id];
  if (item) {
    const data = {id, template: true};
    if (item.required?.custom?.id) {
      const {min, max, best, args} = item.required.custom;
      if (args == null || args === 1) {
        data.custom = [(best === "min" ? min : max) || 1];
      }
    }
    return <D3Tooltip {...props} tooltip={<ItemTooltip data={data}/>}/>;
  } else {
    return props.children;
  }
}

export const TooltipInput = React.forwardRef(({ disabled, min, max, step, onChange, ...props }, ref) => {
  const tip = React.useMemo(() => {
    if (min == null || max == null || min === max) return null;
    let curStep = (step === "any" ? 0.01 : (parseFloat(step) || 1));
    let decimal = 0;
    while (decimal < 2 && Math.floor(curStep - 1e-6) === Math.floor(curStep + 1e-6)) {
      decimal += 1;
      curStep *= 10;
    }
    return <div><span className="d3-color-green">{formatNumber(min, decimal, 10000)}</span>-<span className="d3-color-green">{formatNumber(max, decimal, 10000)}</span></div>;
  }, [min, max, step]);
  const input = <NumberInput {...props} min={min} max={max} step={step} disabled={disabled || (min != null && min === max)} onChange={onChange} ref={ref}/>;

  if (tip) {
    return <Tooltip tooltip={tip}>{input}</Tooltip>;
  } else {
    return input;
  }
});
